/**
 * CR402 Phase 2: Components Tab View
 * CR403: Added ErrorBoundary fallback to prevent admin bricking
 * 
 * Wrapper that renders the ComponentsStudio with proper props from DealCardsTab.
 * The studio provides the 2-pane layout with highlight overlay per CR402 spec.
 */

import { Component, type ReactNode } from "react";
import { ComponentsStudio } from "./ComponentsStudio";
import { ComponentsPanel } from "./ComponentsPanel";
import type { ComponentsConfig, PromoBadgesConfig, DetailsContentConfig } from "./DealCardsTab";
import type { CardStyling } from "./CardStylingPanel";
import type { ExtendedSectionToggles } from "./SectionTogglesPanel";
import type { DealTypeRegistryEntry, DealCardTemplate, CardTemplateLabels } from "@/providers/DealCardConfigProvider";
import type { RibbonDraft } from "./RibbonSettingsPanel";
import type { HeaderComposition } from "./HeaderCompositionPanel";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PublishTargetIndicator } from "@/components/admin/deal-cards/EnvironmentSelector";
import type { PublishEnvironment } from "@/hooks/usePromoteDealCardConfig";

// ================================================================
// ERROR BOUNDARY (CR403: Fallback to Phase 1 if Studio crashes)
// ================================================================

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  componentStack?: string;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
  onReset?: () => void;
}

class ComponentsStudioErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // CR405: Log full error details AND component stack for debugging
    console.error("[CR405] ComponentsStudio crashed:", error.message);
    console.error("Stack:", error.stack);
    console.error("Component stack:", errorInfo.componentStack);
    
    // Store component stack in state for display
    this.setState({ componentStack: errorInfo.componentStack || undefined });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, componentStack: undefined });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="space-y-4">
          <div className="border border-destructive/50 bg-destructive/10 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
              <div className="space-y-2 flex-1">
                <h3 className="font-semibold text-destructive">Components Studio failed to load</h3>
                <p className="text-sm text-muted-foreground">
                  Falling back to Phase 1 Components panel to keep Admin usable.
                </p>
                
                {/* CR404: Show full error + stack trace for debugging */}
                {this.state.error && (
                  <div className="mt-3 space-y-2">
                    <div className="text-xs font-mono whitespace-pre-wrap bg-background border rounded p-3 overflow-auto max-h-32">
                      <span className="font-bold text-destructive">Error: </span>
                      {String(this.state.error?.message || this.state.error)}
                    </div>
                    {this.state.error?.stack && (
                      <details className="text-xs">
                        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                          Show stack trace
                        </summary>
                        <pre className="font-mono whitespace-pre-wrap bg-muted p-2 rounded mt-1 overflow-auto max-h-48 text-[10px]">
                          {this.state.error.stack}
                        </pre>
                      </details>
                    )}
                    {/* CR405: Show component stack to identify which React component crashed */}
                    {this.state.componentStack && (
                      <details className="text-xs">
                        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                          Show component stack (React tree)
                        </summary>
                        <pre className="font-mono whitespace-pre-wrap bg-muted p-2 rounded mt-1 overflow-auto max-h-48 text-[10px]">
                          {this.state.componentStack}
                        </pre>
                      </details>
                    )}
                  </div>
                )}
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={this.handleReset}
                  className="mt-2"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
            </div>
          </div>
          {this.props.fallback}
        </div>
      );
    }

    return this.props.children;
  }
}

// ================================================================
// TYPES
// ================================================================

export interface ComponentsTabViewProps {
  // Context
  registry: DealTypeRegistryEntry[];
  templates: DealCardTemplate[];
  selectedType: string;
  onSelectedTypeChange?: (type: string) => void;
  
  // Draft state
  draftComponents: ComponentsConfig;
  draftStyling: CardStyling;
  draftSections: ExtendedSectionToggles;
  draftSurfaceBlockToggles: { primary: Record<string, boolean>; details: Record<string, boolean> };
  draftRibbon: RibbonDraft;
  draftHeader: HeaderComposition;
  draftLabels: CardTemplateLabels;
  draftSectionOrder: string[];
  draftSurfaceBlockOrder: { primary: string[]; details: string[] };
  themePreset: "light" | "dark" | "custom";
  
  // Saved state for dirty detection
  savedComponents?: ComponentsConfig;
  savedStyling?: CardStyling;
  
  // CR340: Saved ribbon for apply-gating
  savedRibbon?: RibbonDraft;
  
  // CR340: Save trace for debug panel
  lastSaveTrace?: import("@/lib/diagnostics/saveTrace").SaveTrace | null;
  
  // Handlers
  onComponentsChange: (update: Partial<ComponentsConfig>) => void;
  onStylingChange: (update: Partial<CardStyling>) => void;
  onLabelsChange?: (update: Record<string, string>) => void;
  onSave?: () => Promise<void>;
  onDiscard?: () => void;
  
  // State flags
  hasUnsavedChanges: boolean;
  previewKey?: string | number;
  saving?: boolean;
  /** CR416: Which environment this editor publishes to */
  publishEnvironment?: PublishEnvironment;
}

// ================================================================
// COMPONENT
// ================================================================

export function ComponentsTabView({
  registry,
  templates,
  selectedType,
  onSelectedTypeChange,
  draftComponents,
  draftStyling,
  draftSections,
  draftSurfaceBlockToggles,
  draftRibbon,
  draftHeader,
  draftLabels,
  draftSectionOrder,
  draftSurfaceBlockOrder,
  themePreset,
  savedComponents,
  savedStyling,
  savedRibbon,
  lastSaveTrace,
  onComponentsChange,
  onStylingChange,
  onLabelsChange,
  onSave,
  onDiscard,
  hasUnsavedChanges,
  saving = false,
  publishEnvironment = "staging",
}: ComponentsTabViewProps) {
  // CR403: Provide safe default handlers
  const handleSave = onSave || (async () => { console.warn("Save handler not provided"); });
  const handleDiscard = onDiscard || (() => { console.warn("Discard handler not provided"); });
  const handleTypeChange = onSelectedTypeChange || (() => { console.warn("Type change handler not provided"); });
  
  // CR403: Safe defaults for all props to prevent undefined crashes
  const safeRegistry = Array.isArray(registry) ? registry : [];
  const safeTemplates = Array.isArray(templates) ? templates : [];
  const safeDraftComponents = draftComponents ?? {};
  const safeDraftStyling = draftStyling ?? {};
  const safeDraftSections = draftSections ?? {};
  const safeDraftSurfaceBlockToggles = draftSurfaceBlockToggles ?? { primary: {}, details: {} };
  const safeDraftRibbon = draftRibbon ?? { ribbon_text: "DEAL", ribbon_theme: "gray", is_enabled: true };
  const safeDraftHeader = draftHeader ?? {};
  const safeDraftLabels = draftLabels ?? {};
  const safeDraftSectionOrder = Array.isArray(draftSectionOrder) ? draftSectionOrder : [];
  const safeDraftSurfaceBlockOrder = draftSurfaceBlockOrder ?? { primary: [], details: [] };
  const safeThemePreset = themePreset ?? "light";

  // CR403: Phase 1 fallback component (simplified - just pass minimal required props)
  const phase1Fallback = (
    <ComponentsPanel
      components={safeDraftComponents}
      onChange={onComponentsChange}
      structureToggles={{
        header: true,
        ribbon: true,
        starburst: true,
      }}
    />
  );

  return (
    <div className="space-y-4">
      {/* CR416: Publish target indicator */}
      <PublishTargetIndicator environment={publishEnvironment} />
      
      <ComponentsStudioErrorBoundary 
        fallback={phase1Fallback}
        onReset={() => console.log("[CR403] Attempting to recover ComponentsStudio")}
      >
        <ComponentsStudio
        registry={safeRegistry}
        templates={safeTemplates}
        selectedType={selectedType ?? "UNCLASSIFIED"}
        onSelectedTypeChange={handleTypeChange}
        draftComponents={safeDraftComponents}
        draftStyling={safeDraftStyling as CardStyling}
        draftSections={safeDraftSections as ExtendedSectionToggles}
        draftSurfaceBlockToggles={safeDraftSurfaceBlockToggles}
        draftRibbon={safeDraftRibbon as RibbonDraft}
        draftHeader={safeDraftHeader as HeaderComposition}
        draftLabels={safeDraftLabels}
        draftSectionOrder={safeDraftSectionOrder}
        draftSurfaceBlockOrder={safeDraftSurfaceBlockOrder}
        themePreset={safeThemePreset}
        savedComponents={savedComponents}
        savedStyling={savedStyling}
        savedRibbon={savedRibbon}
        lastSaveTrace={lastSaveTrace}
        onComponentsChange={onComponentsChange}
        onStylingChange={onStylingChange}
        onLabelsChange={onLabelsChange}
        onSave={handleSave}
        onDiscard={handleDiscard}
        hasUnsavedChanges={hasUnsavedChanges}
        saving={saving}
        />
      </ComponentsStudioErrorBoundary>
    </div>
  );
}

export default ComponentsTabView;
